import { Compass, Crosshair, Megaphone, Monitor, Plus, ArrowUpRight } from "lucide-react";
import { Overline, Reveal } from "./Reveal";
import { waLink, waSitesLink, WA_MESSAGES } from "../config/site";
import { trackEvent } from "../lib/analytics";

const SERVICES = [
  {
    id: "sites",
    number: "01",
    icon: Monitor,
    title: "Criação de Sites Profissionais",
    description:
      "Sites modernos, rápidos e estratégicos, desenvolvidos para fortalecer sua marca, apresentar seus serviços e transformar visitantes em oportunidades comerciais.",
    includes: [
      "Sites institucionais",
      "Landing pages de alta conversão",
      "Sites para negócios locais",
      "Design responsivo",
      "Formulários comerciais",
      "SEO técnico básico",
      "Integrações com ferramentas de análise",
      "Estrutura preparada para campanhas",
    ],
    cta: "Quero meu site profissional",
    message: WA_MESSAGES.sites,
  },
  {
    id: "trafego",
    number: "02",
    icon: Megaphone,
    title: "Tráfego Pago",
    description:
      "Campanhas estruturadas para colocar sua empresa diante do público certo, gerar oportunidades e melhorar a eficiência dos seus investimentos em publicidade.",
    includes: [
      "Planejamento de campanhas",
      "Google Ads",
      "Meta Ads",
      "Segmentação de público",
      "Campanhas por objetivo",
      "Análise de desempenho",
      "Otimização contínua conforme os dados",
    ],
    cta: "Quero anunciar com estratégia",
    message: WA_MESSAGES.trafego,
  },
  {
    id: "rastreamento",
    number: "03",
    icon: Crosshair,
    title: "Rastreamento de Conversões",
    description:
      "Entenda o que acontece depois do clique. Estruture a mensuração para identificar quais campanhas, páginas e ações geram oportunidades para sua empresa.",
    includes: [
      "Google Analytics 4",
      "Google Tag Manager",
      "Google Ads Conversion Tracking",
      "Meta Pixel",
      "Eventos de conversão",
      "Rastreamento de formulários e cliques",
      "Verificação de disparos e diagnóstico",
      "Integração com outras ferramentas",
    ],
    cta: "Quero medir meus resultados",
    message: WA_MESSAGES.rastreamento,
  },
  {
    id: "estrategia",
    number: "04",
    icon: Compass,
    title: "Estratégia e Crescimento Digital",
    description:
      "Organize sua presença digital com uma estratégia alinhada ao seu negócio, ao seu público e aos seus objetivos comerciais.",
    includes: [
      "Diagnóstico de presença digital",
      "Planejamento de aquisição de clientes",
      "Jornada do cliente",
      "Estruturação de páginas comerciais",
      "Estratégia para negócios locais",
      "Indicadores de desempenho",
      "Recomendações de melhoria contínua",
    ],
    cta: "Quero uma estratégia para minha empresa",
    message: WA_MESSAGES.estrategia,
  },
];

export const Solutions = () => (
  <section id="solucoes" data-testid="solutions-section" className="relative py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
      <Reveal>
        <Overline>Soluções GOBAX</Overline>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 max-w-3xl font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
          Uma estratégia completa.{" "}
          <span className="chrome-text">Um negócio preparado para crescer.</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={0.08 * i} className={i === SERVICES.length - 1 ? "lg:col-start-2" : ""}>
            <article
              data-testid={`solution-card-${s.id}`}
              className="card-glass group flex h-full flex-col p-6 sm:p-8 lg:p-10"
            >
              <div className="flex items-start justify-between">
                <div className="card-glass-icon flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                  <s.icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <span className="chrome-text font-display text-4xl font-extrabold opacity-60">{s.number}</span>
              </div>
              <h3 className="card-glass-title mt-7 font-display text-xl font-bold sm:text-2xl">{s.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#9ca3af] sm:text-base">{s.description}</p>
              <ul className="mt-7 grid flex-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                {s.includes.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px] text-gb-silver/85">
                    <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gb-silver/60" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href={s.id === "sites" ? waSitesLink(s.message) : waLink(s.message)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: `solution_${s.id}` })}
                data-testid={`solution-cta-${s.id}`}
                className="mt-8 inline-flex items-center gap-2 border-b border-gb-silver/40 pb-1 text-sm font-semibold text-gb-silverhi transition-colors duration-300 hover:border-gb-silverhi"
              >
                {s.cta}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </a>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
